import { getLibraryElementDetails } from '@/api/libraries';
import { getTextShapeMask } from '@/api/textShapes';
import type { TextShapeSource } from '@/api/typings/textShape';
import { getMaskUrl, loadMask } from '@/angular-app/libs/BoundedText/TextShape';
import type { BoundedTextElement, TextShapeConfig } from '@/typings/customilyElements/canvasElements';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { buildCloudFrontUrl } from '@/utils/pathUtils';

// Shape a new text to shape starts with, same path the picker would store (DefaultTextShapes.sql)
export const DEFAULT_TEXT_SHAPE_PATH = '/Content/text-shapes/circle.svg';

// A shape only reads as one once the text fills it, so a new element starts with enough text for that
export const DEFAULT_TEXT_SHAPE_TEXT =
    'Text to Shape is perfect for longer text. Watch as your text automatically fills the entire shape, ' +
    'creating a balanced and unique design. Add more words, play around with shape options, and see how ' +
    'the text adapts to the shape while keeping the composition visually appealing.';

// Latest requested position per element, so a slow image load can't override a newer selection
const pendingRequests = new WeakMap<BoundedTextElement, number>();

/**
 * Parses the stored config (JSON string from the API). An empty config is kept, so `!!shapeConfig`
 *  means "the element is a text to shape" - it's created before any shape is picked
 */
export const normalizeTextShapeConfig = (value: unknown): TextShapeConfig | null => {
    let config = value;
    if (typeof config === 'string') {
        try {
            config = JSON.parse(config);
        } catch {
            return null;
        }
    }
    if (!config || typeof config !== 'object') return null;

    const { libraryId, libraryType, shapeMap } = config as Partial<TextShapeConfig>;
    return {
        ...(config as TextShapeConfig),
        libraryId: libraryId || undefined,
        libraryType: libraryId ? libraryType : undefined,
        shapeMap: Array.isArray(shapeMap) ? shapeMap.filter((entry) => Array.isArray(entry) && !!entry[1]) : []
    };
};

/** JSON to persist. An already serialized config is kept as it is, so it's never encoded twice */
export const serializeTextShapeConfig = (config: TextShapeConfig | string | null | undefined): string | null => {
    if (!config) return null;
    return typeof config === 'string' ? config : JSON.stringify(config);
};

/** Sets `paths` on consecutive positions from `startPosition`, replacing the shapes already there */
export const placeShapePaths = (
    shapeMap: [number, string][],
    paths: string[],
    startPosition: number
): [number, string][] => {
    const shapes = new Map(shapeMap);
    paths.forEach((path, index) => shapes.set(startPosition + index, path));
    return [...shapes].sort(([a], [b]) => a - b);
};

export const getTextShapePathAtPosition = async (
    config: TextShapeConfig | null | undefined,
    position: number
): Promise<string | undefined> => {
    if (!config) return undefined;

    if (config.libraryId) {
        const elements = await getLibraryElementDetails(
            config.libraryId,
            config.libraryType ?? LibraryTypeEnum.Image,
            undefined,
            '',
            position
        );
        return elements[0]?.path;
    }
    return new Map(config.shapeMap).get(position);
};

/**
 * Precomputed mask of a shape image (see TextShape.js). Read from the CDN; when it isn't there yet
 *  (eg. an element added to a library after it was picked) the backend generates it on the spot
 */
export const loadTextShapeMask = (url: string, source: TextShapeSource) =>
    loadMask(url, async () => {
        try {
            const response = await fetch(getMaskUrl(url));
            if (response.ok) return await response.json();
        } catch {
            // CDN unreachable (CORS, network) or not a mask file (eg. an error page): the backend still serves it
        }
        return getTextShapeMask(source);
    });

/**
 * Why the backend refused to make a mask: the image can never be a shape (nothing visible, damaged, unsupported
 *  format). undefined for any other failure, which may work on another try
 */
export const getTextShapeRejection = (error: unknown): string | undefined => {
    const response = (error as { response?: { status?: number; data?: { Message?: string } } } | undefined)?.response;
    if (response?.status !== 400) return undefined;
    return response.data?.Message || 'The image can not be used as a shape';
};

/**
 * Applies the shape at `position` of the element's shapeConfig, or clears the shape when there's
 *  nothing to apply. `path` skips the lookup when the caller already knows it.
 */
export const applyTextShapeOption = async (element: BoundedTextElement, position = 1, path?: string) => {
    if (!element) return;

    const requestId = (pendingRequests.get(element) || 0) + 1;
    pendingRequests.set(element, requestId);

    element.shapeOption = position;
    const shapePath = path || (await getTextShapePathAtPosition(element.shapeConfig, position));
    if (pendingRequests.get(element) !== requestId) return;

    if (!element.shapeConfig || !shapePath) {
        element.clearTextShape();
        return;
    }
    const url = buildCloudFrontUrl(shapePath);
    if (url === element.textShape) return;
    const { libraryId } = element.shapeConfig;
    const source: TextShapeSource = libraryId ? { libraryId, position } : { path: shapePath };
    let mask;
    try {
        mask = await loadTextShapeMask(url, source);
    } catch (error) {
        // Same as the storefront and the production file: an image that can't be a shape leaves the rectangle
        if (getTextShapeRejection(error) && pendingRequests.get(element) === requestId) element.clearTextShape();
        throw error;
    }
    // Checked again: the mask load is the long part
    if (pendingRequests.get(element) !== requestId) return;
    element.setTextShapeMask(mask, url);
};
